import type { CSSProperties, ReactNode } from "react";

// The round image control that both the person's photo and the company's logo are.
//
// They were the same interaction written twice: a circular thing you click to replace, with the
// instruction hidden until you hover it. The person's version came first (Save · Basics); the
// company page grew its own with a sentence of prose underneath instead of the overlay. This is the
// person's behaviour, extracted so there is one of it — CLAUDE.md's rule is to deepen the existing
// primitive rather than grow a second.
//
// WHAT IT DELIBERATELY DOES NOT OWN: the picker. The person side holds a hidden <input type="file">,
// the company side opens a FileDropZone that also accepts a drop. Both are legitimate and neither is
// this component's business, so callers own the picker and pass `onActivate`.
//
// The image itself is `children` for the same reason: one side draws an <img> or letter initials,
// the other delegates to CompanyLogo, which has its own monogram rules.

export function ImageUpload({
  editable,
  busy = false,
  hoverLabel,
  ariaLabel,
  onActivate,
  remove = null,
  style,
  children,
}: {
  editable: boolean;
  /** A write is in flight; the control stays visible but refuses a second one. */
  busy?: boolean;
  /** What the hover overlay says, e.g. "Change photo". Also the title, so it survives a slow read. */
  hoverLabel: string;
  /**
   * The control's accessible name. Needed because the visible content is an image or initials —
   * there is no text for a screen reader to announce. Falls back to `hoverLabel`.
   */
  ariaLabel?: string;
  onActivate: () => void;
  /**
   * The remove affordance, or null for none. A caller that omits it is saying the image can be
   * replaced but not taken away — the company logo is deliberately that, the person's photo is not.
   */
  remove?: { label: string; onRemove: () => void } | null;
  style?: CSSProperties;
  children: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center gap-2">
      <button
        type="button"
        onClick={editable ? onActivate : undefined}
        disabled={!editable || busy}
        title={editable ? hoverLabel : undefined}
        aria-label={editable ? (ariaLabel ?? hoverLabel) : undefined}
        // Neutral throughout: a hairline ring at rest says "control", the focus ring is the app's
        // usual one, and the hover state is the overlay rather than a colour. No accent — this is a
        // state, not a signal.
        //
        // Opacity is keyed on `busy`, not on `disabled`/`editable`: `editable=false` is a permanent
        // read-only state (someone else's photo) and must render at full strength — only an in-flight
        // write (`busy`) should visibly dim the control.
        className={`group/upload relative flex items-center justify-center overflow-hidden rounded-full ring-1 ring-ink/10 transition focus:outline-none focus-visible:ring-2 focus-visible:ring-ink/30 ${
          busy ? "opacity-50" : ""
        } ${editable ? "cursor-pointer" : "cursor-default"}`}
        style={style}
      >
        {children}
        {editable ? (
          <span className="absolute inset-0 flex items-center justify-center rounded-full bg-ink/40 text-[11px] tracking-[0.05em] text-on-ink opacity-0 transition group-hover/upload:opacity-100">
            {hoverLabel}
          </span>
        ) : null}
      </button>
      {editable && remove ? (
        <button
          type="button"
          onClick={remove.onRemove}
          className="text-[11px] tracking-[0.05em] text-ink/45 transition hover:text-accent"
        >
          {remove.label}
        </button>
      ) : null}
    </div>
  );
}
